import { useState } from 'react';
import { Group, Button, TextInput, NumberInput, MultiSelect, Select, Fieldset } from '@mantine/core';
import { useForm } from '@mantine/form';

import { useAppSelector, selectCurrencies } from '@/shared';
import {
  partnerCommissionCalculationBases,
  partnerCommissionCalculationBaseTitles,
  shopTypes,
  shopTypeTitles,
} from '../utils/const/common';
import { getPartnerCommissions } from '../utils/helpers/partner-commissions';

import type { TPartnerCommission, TShopCreateParams, TShopType } from '../store/types';

type TPartnerCommissionForm = Omit<TPartnerCommission, 'commissionPercent'> & {
  commissionPercent: number | string;
};

type FormData = Omit<TShopCreateParams, 'partnerCommissions'> & {
  partnerCommissions: TPartnerCommissionForm[];
};

type Props = {
  onSubmit: (params: TShopCreateParams) => void;
  onClose: () => void;
};

export const CreateShopForm = (props: Props) => {
  const { onSubmit, onClose } = props;
  const avalableCurrencies = useAppSelector(selectCurrencies);
  const [shopType, setShopType] = useState<TShopType>('regular');
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);

  const form = useForm<FormData>({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      currencies: [],
      type: 'regular',
      partnerCommissions: [],
      callbackUrl: '',
      ownerId: undefined,
      homepageUrl: '',
      successUrl: '',
      failureUrl: '',
    },
    validate: {
      name: (value) => !value ? 'Введите название магазина' : null,
      currencies: (value) => value.length === 0 ? 'Выберите валюту/ты' : null,
      ownerId: (value, values) => values.type === 'partner' && !value
        ? 'Введите ID владельца партнёрского магазина'
        : null,
      callbackUrl: (value) => !value ? 'Введите Callback URL' : null,
      homepageUrl: (value) => !value ? 'Введите URL домашней страницы' : null,
      successUrl: (value) => !value ? 'Введите URL для успешной операции' : null,
      failureUrl: (value) => !value ? 'Введите URL для неудачной операции' : null,
      partnerCommissions: {
        calculationBase: (value, values) => values.type === 'partner' && !value
          ? 'Выберите базу расчёта'
          : null,
        commissionPercent: (value, values) => {
          if (values.type !== 'partner') return null;
          if (value === '' || Number.isNaN(Number(value))) return 'Введите процент комиссии';
          if (Number(value) < 0 || Number(value) > 100) return 'Процент должен быть от 0 до 100';

          return null;
        },
      },
    },
  });

  form.watch('type', ({ value }) => {
    setShopType(value);
  });

  form.watch('currencies', ({ value }) => {
    setSelectedCurrencies(value);
    form.setFieldValue(
      'partnerCommissions',
      getPartnerCommissions(value, form.getValues().partnerCommissions),
    );
  });

  const handleSubmit = (values: FormData) => {
    const params: TShopCreateParams = {
      ...values,
      ownerId: values.ownerId ? values.ownerId : undefined,
      partnerCommissions: values.type === 'partner'
        ? values.partnerCommissions.map((commission) => ({
          ...commission,
          commissionPercent: Number(commission.commissionPercent),
        }))
        : [],
    };

    onSubmit(params);
    onClose();
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <TextInput
        label="Название магазина"
        withAsterisk
        key={form.key('name')}
        {...form.getInputProps('name')}
      />
      <Select
        label="Тип магазина"
        withAsterisk
        allowDeselect={false}
        data={shopTypes.map((type) => ({ value: type, label: shopTypeTitles[type] }))}
        key={form.key('type')}
        {...form.getInputProps('type')}
      />
      <MultiSelect
        label="Валюты (множественный выбор)"
        clearable
        withAsterisk
        data={avalableCurrencies.map((currency) => ({ value: currency, label: currency }))}
        key={form.key('currencies')}
        {...form.getInputProps('currencies')}
      />
      <NumberInput
        label="ID владельца"
        withAsterisk={shopType === 'partner'}
        allowDecimal={false}
        min={1}
        key={form.key('ownerId')}
        {...form.getInputProps('ownerId')}
      />
      {shopType === 'partner' && selectedCurrencies.length > 0 && (
        <Fieldset legend="Партнёрские комиссии" mt="md" styles={{ root: { padding: '10px' } }}>
          {selectedCurrencies.map((currency, index) => (
            <Group key={currency} grow align="flex-start">
              <NumberInput
                label={`${currency} (%)`}
                decimalScale={10}
                step={0.1}
                min={0}
                max={100}
                key={form.key(`partnerCommissions.${index}.commissionPercent`)}
                {...form.getInputProps(`partnerCommissions.${index}.commissionPercent`)}
              />
              <Select
                label="База расчёта"
                allowDeselect={false}
                data={partnerCommissionCalculationBases.map((calculationBase) => ({
                  value: calculationBase,
                  label: partnerCommissionCalculationBaseTitles[calculationBase],
                }))}
                key={form.key(`partnerCommissions.${index}.calculationBase`)}
                {...form.getInputProps(`partnerCommissions.${index}.calculationBase`)}
              />
            </Group>
          ))}
        </Fieldset>
      )}
      <TextInput
        label="Callback URL"
        withAsterisk
        key={form.key('callbackUrl')}
        {...form.getInputProps('callbackUrl')}
      />
      <TextInput
        label="URL домашней страницы"
        withAsterisk
        key={form.key('homepageUrl')}
        {...form.getInputProps('homepageUrl')}
      />
      <TextInput
        label="URL для редиректа успешного выполнения операции"
        withAsterisk
        key={form.key('successUrl')}
        {...form.getInputProps('successUrl')}
      />
      <TextInput
        label="URL для редиректа неудачного выполнения операции"
        withAsterisk
        key={form.key('failureUrl')}
        {...form.getInputProps('failureUrl')}
      />

      <Group justify="flex-end" mt="30">
        <Button variant="outline" onClick={() => onClose()}>
          Отменить
        </Button>
        <Button type="submit">
          Добавить
        </Button>
      </Group>
    </form>
  );
};
